/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { ArrowLeftOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AlertConfig } from '@/client';
import { ContentContainer } from '@/components/ContentContainer';
import { useMessage } from '@/hooks/message';
import { useUpdateAlertConfigMutation } from '@/hooks/mutation/useUpdateAlertConfigMutation';
import { useAlertConfigQuery } from '@/hooks/query/useAlertConfigQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Container } from '@/primitives/Container';
import { Tabs } from '@/primitives/Tabs';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { tokens } from '@/theme';

import { AlertConfigDetailsOverview } from './AlertConfigDetailsOverview';
import { AlertConfigDetailsResponses } from './AlertConfigDetailsResponses';
import { DestinationType } from './constants';
import { CreateAlertWizard } from './CreateAlertWizard';
import { DeleteAlertConfigModal } from './DeleteAlertConfigModal';

import './AlertConfigDetailsPage.css';

export function AlertConfigDetailsPage() {
  const { t } = useMimicTranslation('notificationManagement');
  const { alertId, tenantID } = useParams<{ alertId: string; tenantID: string }>();
  const navigate = useNavigate();
  const [messageApi] = useMessage();
  const [editWizardOpen, setEditWizardOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<AlertConfig | undefined>();

  const alertConfigQuery = useAlertConfigQuery(alertId);
  const alert = alertConfigQuery.data?.data;

  const { mutate: updateAlert, isPending } = useUpdateAlertConfigMutation({
    onSuccess: () => {
      messageApi.success(t('details.statusUpdated'));
    },
    onError: () => {
      messageApi.error(t('details.statusUpdateError'));
    },
  });

  const handleBack = () => {
    navigate(`/tenants/${tenantID}/notifications/management`);
  };

  const handleSuspendToggle = () => {
    if (!alert || !alertId) return;
    const newStatus = alert.status === 'active' ? 'suspended' : 'active';

    updateAlert({
      id: alertId,
      requestBody: { status: newStatus },
    });
  };

  const handleDeleteClick = () => {
    if (alert) {
      setAlertToDelete(alert);
    }
  };

  const handleDeleteCancel = () => {
    setAlertToDelete(undefined);
  };

  const handleDeleteSuccess = () => {
    navigate(`/tenants/${tenantID}/notifications/management`);
  };

  if (alertConfigQuery.isLoading) {
    return (
      <ContentContainer width="100%">
        <Container full style={{ width: '100%', padding: '48px', textAlign: 'center' }}>
          <Spin size="large" />
        </Container>
      </ContentContainer>
    );
  }

  if (!alert) {
    return (
      <ContentContainer width="100%">
        <Container>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('details.back')}
          </Button>
          <Text>{t('details.notFound')}</Text>
        </Container>
      </ContentContainer>
    );
  }

  const tabs = [
    {
      key: '1',
      label: t('details.overview'),
      children: <AlertConfigDetailsOverview alert={alert} />,
    },
    {
      key: '2',
      label: t('details.responses'),
      children: <AlertConfigDetailsResponses alertId={alertId!} />,
    },
  ];

  return (
    <ContentContainer width="100%">
      <Container gap="sm" full style={{ width: '100%' }}>
        <div className="alert-config-details-back">
          <Button type="inline-link" icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('details.back')}
          </Button>
        </div>

        <div className="alert-config-details-header">
          <div className="alert-config-details-header-info">
            <Title level={2}>{alert.name}</Title>
          </div>

          <div className="alert-config-details-header-actions">
            <Button type="default" onClick={() => setEditWizardOpen(true)}>
              {t('details.edit')}
            </Button>
            <Button
              type="primary"
              onClick={handleSuspendToggle}
              loading={isPending}
              style={{ backgroundColor: '#191919', borderColor: '#191919' }}
            >
              {alert.status === 'active' ? t('details.suspendConnection') : t('details.activateConnection')}
            </Button>
            <Button
              type="primary"
              onClick={handleDeleteClick}
              disabled={isPending}
              style={{ backgroundColor: tokens.color.red.red07, borderColor: tokens.color.red.red07 }}
            >
              {t('details.deleteConnection')}
            </Button>
          </div>
        </div>

        <Tabs items={tabs} style={{ paddingTop: '16px', width: '100%' }} />
      </Container>

      {editWizardOpen && alert && (
        <CreateAlertWizard
          destinationType={alert.type as DestinationType}
          onClose={() => setEditWizardOpen(false)}
          editAlert={alert}
        />
      )}

      {alertToDelete && (
        <DeleteAlertConfigModal
          alertId={alertToDelete.id}
          alertName={alertToDelete.name}
          onCancelDeletion={handleDeleteCancel}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
    </ContentContainer>
  );
}
